/**
 * @component ry-step
 * @category  nav
 * @summary   Horizontal (or vertical) step indicator for multi-page flows (Fomantic UI `.ui.steps` equivalent). Authored as a wrapper with direct children — each child becomes a step. Mark a child with `[active]` for the current step, `[completed]` for finished steps, `[disabled]` for blocked ones. The wrapper sets `aria-current="step"` on the active item and applies role/listitem semantics.
 *
 * Reference: Fomantic UI's step module. ry-step keeps the wrapper + child
 * convention but uses attributes (`[active]`, `[completed]`, `[disabled]`)
 * on children instead of CSS classes. Layout variants live on the wrapper.
 *
 * @attribute vertical   boolean   default=false   Stack steps vertically instead of horizontally.
 * @attribute fluid      boolean   default=false   Stretch the step row to fill its container; children share width equally.
 * @attribute ordered    boolean   default=false   Render a numeric prefix (`1.`, `2.`, ...) before each step title via CSS counter.
 * @attribute size       'sm' | 'lg'?              CSS hook for compact / enlarged steps.
 *
 * @content   Each direct child is one step. Children may be plain `<div>`, `<a href="...">` (for navigable steps), or any element. ry-step tags each as `data-ry-target="step"` + `role="listitem"`. Inside each step, consumers can place their own icon/title/description markup.
 *
 * @dom
 *   ry-step[vertical?][fluid?][ordered?][size?]   role="list"   aria-label="Progress"?
 *     > {step 1}    data-ry-target="step"   role="listitem"  [active]? aria-current="step"?  [completed]?  [disabled]?
 *     > {step 2}    data-ry-target="step"   role="listitem"  ...
 *     > ...
 *
 * @gotcha  ARIA `aria-current="step"` is applied to the `[active]` child automatically — do NOT also author it. Only one child should have `[active]` at a time; if multiple, the LAST one with `[active]` wins (it's the only one that retains the attribute when the wrapper re-applies its semantics).
 *
 * @gotcha  `[disabled]` does NOT block clicks at the JS layer — it's a CSS/state hook for theme styling and `aria-disabled="true"`. If the child is a link, the consumer must add `pointer-events: none` via theme, OR intercept click on the parent. Don't put `<a href>` inside `[disabled]` steps and expect navigation to silently no-op.
 *
 * @gotcha  Setup-once: dynamically inserted children after mount do NOT get `data-ry-target="step"` or `role="listitem"`. Re-mount or insert pre-tagged markup. Live update of `[active]` between EXISTING children IS supported — the wrapper observes attribute changes and re-applies aria-current.
 *
 * @gotcha  `[ordered]` renders numbers via CSS counter. The number reflects DOM order, not a custom step index. To start the count at a non-1 number, set `--ry-step-start` on the wrapper.
 *
 * @example basic
 * ```html
 * <ry-step>
 *   <div completed>Shipping</div>
 *   <div active>Billing</div>
 *   <div>Confirm</div>
 * </ry-step>
 * ```
 *
 * @example ordered
 * ```html
 * <ry-step ordered>
 *   <div completed>Cart</div>
 *   <div completed>Address</div>
 *   <div active>Payment</div>
 *   <div>Review</div>
 * </ry-step>
 * ```
 *
 * @example vertical
 * ```html
 * <ry-step vertical>
 *   <div completed>Step 1</div>
 *   <div active>Step 2</div>
 *   <div disabled>Step 3 (locked)</div>
 * </ry-step>
 * ```
 *
 * @example fluid
 * ```html
 * <ry-step fluid>
 *   <div completed>One</div>
 *   <div active>Two</div>
 *   <div>Three</div>
 * </ry-step>
 * ```
 *
 * @example navigable-with-links
 * ```html
 * <ry-step>
 *   <a href="/cart" completed>Cart</a>
 *   <a href="/address" active>Address</a>
 *   <a href="/pay" disabled>Pay</a>
 * </ry-step>
 * ```
 *
 * @tutorial mount-tags-children-as-steps
 * ```js
 * const el = mount(`
 *   <ry-step>
 *     <div>A</div>
 *     <div>B</div>
 *     <div>C</div>
 *   </ry-step>
 * `);
 * const steps = el.querySelectorAll('[data-ry-target="step"]');
 * await expectEqual(steps.length, 3);
 * ```
 *
 * @tutorial active-child-gets-aria-current
 * ```js
 * const el = mount(`
 *   <ry-step>
 *     <div completed>A</div>
 *     <div active>B</div>
 *     <div>C</div>
 *   </ry-step>
 * `);
 * const steps = el.querySelectorAll('[data-ry-target="step"]');
 * await expectEqual(steps[0].hasAttribute('aria-current'), false);
 * await expectAttr(steps[1], 'aria-current', 'step', 'active step is aria-current="step"');
 * await expectEqual(steps[2].hasAttribute('aria-current'), false);
 * ```
 *
 * @tutorial no-active-step-no-aria-current
 * ```js
 * // Wrong assumption: ry-step always picks an active child if none is set.
 * // It does NOT — without an [active] attribute, no aria-current is applied.
 * const el = mount(`
 *   <ry-step>
 *     <div>A</div>
 *     <div>B</div>
 *   </ry-step>
 * `);
 * const steps = el.querySelectorAll('[data-ry-target="step"]');
 * await expectEqual(steps[0].hasAttribute('aria-current'), false);
 * await expectEqual(steps[1].hasAttribute('aria-current'), false);
 * ```
 *
 * @tutorial disabled-applies-aria-disabled
 * ```js
 * const el = mount(`
 *   <ry-step>
 *     <div completed>A</div>
 *     <div active>B</div>
 *     <div disabled>C</div>
 *   </ry-step>
 * `);
 * const steps = el.querySelectorAll('[data-ry-target="step"]');
 * await expectAttr(steps[2], 'aria-disabled', 'true');
 * await expectEqual(steps[0].hasAttribute('aria-disabled'), false);
 * await expectEqual(steps[1].hasAttribute('aria-disabled'), false);
 * ```
 *
 * @tutorial host-gets-role-list-and-aria-label
 * ```js
 * const el = mount('<ry-step><div>A</div></ry-step>');
 * await expectAttr(el, 'role', 'list', 'host role="list" for step group');
 * await expectAttr(el, 'aria-label', 'Progress', 'auto aria-label');
 * ```
 *
 * @tutorial preserves-explicit-aria-label
 * ```js
 * const el = mount('<ry-step aria-label="Checkout"><div>A</div></ry-step>');
 * await expectAttr(el, 'aria-label', 'Checkout', 'explicit aria-label preserved');
 * ```
 *
 * @tutorial live-active-change-moves-aria-current
 * ```js
 * // Wrong assumption: [active] is read once. ry-step observes child
 * // attribute changes and moves aria-current to the new active step.
 * const el = mount(`
 *   <ry-step>
 *     <div id="a" active>A</div>
 *     <div id="b">B</div>
 *   </ry-step>
 * `);
 * const a = el.querySelector('#a');
 * const b = el.querySelector('#b');
 * await expectAttr(a, 'aria-current', 'step');
 * await expectEqual(b.hasAttribute('aria-current'), false);
 * a.removeAttribute('active');
 * b.setAttribute('active', '');
 * // Wait a tick for the MutationObserver to fire
 * await sleep(20);
 * await expectEqual(a.hasAttribute('aria-current'), false, 'old active loses aria-current');
 * await expectAttr(b, 'aria-current', 'step', 'new active gets aria-current');
 * ```
 *
 * @see ry-breadcrumb
 * @see ry-tabs
 */

import { RyElement } from '../core/ry-element.js';

export class RyStep extends RyElement {
  #observer: MutationObserver | null = null;

  setup(): void {
    this.#applyAria();
    this.#tagChildren();
    this.#syncActive();
    this.#observeChildren();
  }

  teardown(): void {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'list');
    if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Progress');
  }

  #tagChildren(): void {
    Array.from(this.children).forEach((child) => {
      const el = child as HTMLElement;
      if (!el.hasAttribute('data-ry-target')) el.setAttribute('data-ry-target', 'step');
      if (!el.hasAttribute('role')) el.setAttribute('role', 'listitem');
    });
  }

  #syncActive(): void {
    const steps = this.$$<HTMLElement>('[data-ry-target="step"]');
    steps.forEach((step) => {
      if (step.hasAttribute('active')) {
        step.setAttribute('aria-current', 'step');
      } else if (step.getAttribute('aria-current') === 'step') {
        step.removeAttribute('aria-current');
      }
      if (step.hasAttribute('disabled')) {
        step.setAttribute('aria-disabled', 'true');
      } else if (step.getAttribute('aria-disabled') === 'true') {
        step.removeAttribute('aria-disabled');
      }
    });
  }

  #observeChildren(): void {
    this.#observer = new MutationObserver(() => this.#syncActive());
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['active', 'disabled'],
      subtree: true,
    });
  }
}

customElements.define('ry-step', RyStep);
